import React, { useMemo, useState } from "react";
import { Divider, ListItemIcon, Menu, MenuItem, useTheme } from "@mui/material";
import { MoreHorizontal } from "lucide-react";
import { ButtonIconOnly } from "src/UI/ButtonIconOnly";
import { ListItemAction } from "./types";

interface MoreActionsMenuProps {
  actions?: ListItemAction[] | ListItemAction[][];
  disabled?: boolean;
  triggerTestId?: string;
}

const MoreActionsMenu: React.FC<MoreActionsMenuProps> = ({
  actions,
  disabled,
  triggerTestId,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const groups: ListItemAction[][] = useMemo(() => {
    if (!actions || actions.length === 0) return [];
    const isGrouped = Array.isArray(actions[0]);
    return (isGrouped ? actions : [actions]) as ListItemAction[][];
  }, [actions]);

  if (groups.length === 0) return null;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (actionFn: () => void) => {
    handleClose();
    actionFn();
  };

  return (
    <>
      <ButtonIconOnly
        {...(triggerTestId
          ? { id: triggerTestId, "data-testid": triggerTestId }
          : {})}
        onClick={handleOpen}
        disabled={disabled}
        iconOnly={<MoreHorizontal size={16} />}
      />
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {groups.map((group, groupIndex) => [
          groupIndex > 0 && (
            <Divider key={`divider-${groupIndex}`} sx={{ my: "4px" }} />
          ),
          ...group.map((action, actionIndex) => (
            <MenuItem
              key={`${groupIndex}-${actionIndex}`}
              {...(action.testId
                ? { id: action.testId, "data-testid": action.testId }
                : {})}
              disabled={action.disabled}
              onClick={() => handleAction(action.onClick)}
              sx={{
                fontSize: "13px",
                color: theme.palette.text.primary,
                gap: "10px",
              }}
            >
              {action.icon && (
                <ListItemIcon
                  sx={{
                    minWidth: "auto !important",
                    color: theme.palette.text.primary,
                  }}
                >
                  {action.icon}
                </ListItemIcon>
              )}
              {action.label}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};

export default MoreActionsMenu;
